import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const PermissionMatrix = () => {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [assigned, setAssigned] = useState({}); // {role_id: Set(perm_id)}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API}/admin/enterprise/roles`, headers()),
        axios.get(`${API}/admin/enterprise/permissions`, headers()),
      ]);
      setRoles(r.data.roles);
      setPerms(p.data.permissions);
      const map = {};
      for (const role of r.data.roles) {
        map[role.id] = new Set();
      }
      // fetch current assignments per role
      for (const role of r.data.roles) {
        try {
          const res = await axios.get(`${API}/admin/enterprise/roles/${role.id}/permissions`, headers());
          (res.data.permission_ids || []).forEach((pid) => map[role.id].add(pid));
        } catch {}
      }
      setAssigned(map);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (roleId, permId) => {
    const next = new Set(assigned[roleId]);
    if (next.has(permId)) next.delete(permId); else next.add(permId);
    setAssigned({ ...assigned, [roleId]: next });
    try {
      await axios.post(`${API}/admin/enterprise/role-permissions`, { role_id: roleId, permission_id: permId }, headers());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load(); // revert
    }
  };

  if (loading) return <LoaderCircle className="mt-6 h-5 w-5 animate-spin text-[#00D4FF]" />;

  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="permission-matrix">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><ShieldCheck className="h-5 w-5 text-[#00FFD1]" /> Permission Matrix</p>
      <p className="mt-1 text-xs text-slate-500">Click a cell to grant or revoke a permission for a role.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr>
              <th className="p-2 text-left font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Permission</th>
              {roles.map((r) => (
                <th key={r.id} className="p-2 text-center font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{r.role_name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perms.map((p) => (
              <tr key={p.id} className="border-t border-white/5">
                <td className="p-2 text-xs text-slate-300">
                  <span className="font-bold">{p.permission_name}</span>
                  <span className="ml-2 font-jbmono text-[9px] text-slate-600">{p.module_name}</span>
                </td>
                {roles.map((r) => (
                  <td key={r.id} className="p-2 text-center">
                    <button
                      data-testid={`pm-${r.id.slice(-4)}-${p.id.slice(-4)}`}
                      onClick={() => toggle(r.id, p.id)}
                      className={`h-5 w-5 rounded-md border transition-all ${assigned[r.id]?.has(p.id) ? "border-[#00FFD1] bg-[#00FFD1]/20 text-[#00FFD1]" : "border-white/20 bg-transparent text-transparent hover:border-white/40"}`}
                    >
                      ✓
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
